import React from "react";
import { Star, Quote, Phone } from "lucide-react";
import { SITE, IMAGES, TESTIMONIALS } from "@/lib/site";
import SectionTitle from "@/components/SectionTitle";
import usePageMeta from "@/hooks/usePageMeta";

export default function AvisPage() {
  usePageMeta(
    "Avis clients — témoignages des familles",
    "Découvrez les témoignages des familles que nous avons accompagnées. Un service humain et respectueux à Baho, Perpignan et alentours."
  );
  return (
    <div data-testid="avis-page">
      <section className="relative bg-black text-white py-28 lg:py-40">
        <div className="absolute inset-0 opacity-25">
          <img src={IMAGES.hands} alt="" className="w-full h-full object-cover grayscale" />
        </div>
        <div className="relative container-soft">
          <p className="overline text-white/60">Avis clients</p>
          <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl mt-4 max-w-4xl tracking-tight leading-[1.02]">
            Les mots des familles <em className="italic font-light">qui nous ont confié leur peine</em>.
          </h1>
          <p className="mt-8 max-w-2xl text-white/80 text-lg leading-relaxed">
            Chaque message reçu nous rappelle pourquoi nous faisons ce métier.
            Voici quelques témoignages, partagés avec leur permission.
          </p>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="container-soft">
          <div className="grid md:grid-cols-2 gap-px bg-black/10 border border-black/10">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={i}
                data-testid={`avis-${i}`}
                className="bg-white p-10 lg:p-12 hover:bg-neutral-50 transition-colors duration-500"
              >
                <Quote size={32} strokeWidth={1} className="text-black/30" />
                <div className="flex gap-1 mt-6">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} size={14} strokeWidth={1.25} fill="currentColor" />
                  ))}
                </div>
                <blockquote className="mt-6 font-serif-display text-2xl lg:text-3xl leading-snug">
                  « {t.text} »
                </blockquote>
                <figcaption className="mt-10 text-sm text-neutral-500 tracking-wide">
                  — {t.name}, {t.city}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-neutral-50 py-24 lg:py-32 border-y border-black/10">
        <div className="container-soft text-center max-w-3xl mx-auto">
          <p className="overline">Un mot à partager ?</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.08]">
            Votre témoignage compte pour nous.
          </h2>
          <p className="mt-6 text-neutral-700 text-lg leading-relaxed">
            Si nous avons eu l'honneur de vous accompagner, n'hésitez pas à partager
            votre ressenti. Cela aide d'autres familles à trouver le soutien qu'elles cherchent.
          </p>
          <a
            href={`mailto:${SITE.email}?subject=Mon%20témoignage`}
            data-testid="avis-share-link"
            className="mt-10 inline-flex items-center justify-center gap-2 border border-black px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-black hover:text-white transition-all duration-300"
          >
            Partager mon témoignage
          </a>
        </div>
      </section>

      <section className="py-24 lg:py-32 text-center">
        <div className="container-soft">
          <SectionTitle
            overline="Une question ?"
            title="Nous sommes joignables à toute heure."
            align="center"
          />
          <div className="mt-12">
            <a href={SITE.phoneHref} data-testid="avis-cta-call" className="inline-flex items-center justify-center gap-3 bg-black text-white px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-neutral-800 transition-all duration-300">
              <Phone size={18} strokeWidth={1.5} /> {SITE.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
